import React, { useState, useRef, useEffect } from "react";
import { Button, Modal } from "antd";
import { FiType, FiActivity, FiUsers, FiBriefcase } from "react-icons/fi";
import { FaApple } from "react-icons/fa";

const RELEASE_SECTIONS = [
  {
    tab: "Mac",
    icon: <FaApple />,
    launch: true,
    pill: "Industry First",
    heroCaption: "Now on Mac",
    title: "Clarity RadiScan is now on Mac",
    lead: "The first radiology reporting platform to run on Mac. Everything you do on Windows now works on a Mac too.",
    groups: [
      {
        points: [
          "Full reporting workflow \u2014 worklists, dictation, verification and release",
          "Images in InteleViewer, with the Multipacs RadiScan Service for Mac",
          "Requires a Mac with Apple Silicon (M1 or later)",
        ],
      },
    ],
  },
  {
    tab: "Everyone",
    icon: <FiUsers />,
    title: "For Everyone",
    groups: [
      {
        heading: "Filters and Search",
        points: [
          {
            title: "Redesigned Filters",
            text: "The filters at the top of each page have a new look and work the same way on every page. You can also pick your own date range.",
          },
          {
            title: "Search by Study Description",
            text: "You can now search by study description, not just patient name, PID or visit number.",
          },
        ],
      },
      {
        heading: "Images",
        points: [
          {
            title: "Images Open Correctly After Auto-Next",
            text: "Images now open correctly when auto-next moves you to the next case. Previously they sometimes didn't open, or opened for the wrong site.",
          },
        ],
      },
      {
        heading: "Report Hold Time",
        points: [
          {
            title: "Hold Time Set Per Site",
            text: "The time a verified report waits before being sent to the RIS can now be set differently for each site. If your site needs a longer or shorter hold time, contact the Clarity team.",
          },
        ],
      },
    ],
  },
  {
    tab: "Radiologists",
    icon: <FiActivity />,
    title: "For Radiologists",
    groups: [
      {
        heading: "Reporting",
        points: [
          {
            title: "Faster Auto-Next",
            text: "After you verify, park or send to the typist, the next case loads much sooner.",
          },
          {
            title: "Verify Instantly",
            text: "Click the arrow on the Verify button and choose \"Verify Instantly\" to send the report to the RIS straight away, without the usual hold period. Use this when a report needs to go out immediately. Note: once sent, the report can't be undone from Clarity.",
          },
          {
            title: "Release Now",
            text: "If one of your reports is sitting on Hold and you want it sent now, open Global Search, or the Quick Search icon on the report page, and click \"Release Now\" next to it. Note: once sent, the report can't be undone from Clarity.",
          },
          {
            title: "Second Opinion",
            text: "A new \"Second Opinion\" checkbox is available on the report page, next to Co-Report. Tick it to mark a report as a second opinion.",
          },
        ],
      },
      {
        heading: "Images and Search",
        points: [
          {
            title: "Choose Whether Images Open Automatically",
            text: "In your Default Configurations there's a new switch, \"Auto-Launch InteleViewer on To Be Verified\". Turn it off if you don't want InteleViewer opening every time you open a case from To Be Verified. You can still open images with the View Images button.",
          },
          {
            title: "Search as You Type",
            text: "Search results now appear as you type.",
          },
        ],
      },
    ],
  },
  {
    tab: "Typists",
    icon: <FiType />,
    title: "For Typists",
    groups: [
      {
        heading: "Typing",
        points: [
          {
            title: "Download Dictation Audio",
            text: "You can download the dictation audio from the Report Editor.",
          },
          {
            title: "Search as You Type",
            text: "Search results now appear as you type.",
          },
          {
            title: "Patient Name and Parked By",
            text: "The Parked and Workload Overview pages now show the patient's name and who parked the case.",
          },
        ],
      },
    ],
  },
  {
    tab: "Practice Admins",
    icon: <FiBriefcase />,
    title: "For Practice Admins",
    groups: [
      {
        heading: "Global Search",
        points: [
          {
            title: "State Filter",
            text: "Global Search now has a State filter, so you can see only cases in a particular state (for example, On Hold).",
          },
          {
            title: "Release Now",
            text: "Reports on Hold for your sites can be sent to the RIS immediately using the new \"Release Now\" button in Global Search. Once sent, the report can't be undone from Clarity.",
          },
        ],
      },
      {
        heading: "Reports and Lists",
        points: [
          {
            title: "Download Draft Report",
            text: "You can now download a draft (unverified) report from Clarity, for example to share a preliminary result before the report is finalised.",
          },
          {
            title: "Parked List",
            text: "The Parked list now shows who parked each case, and you can unpark cases.",
          },
          {
            title: "Names on the Locked Page",
            text: "The Locked page now shows radiologist and user names instead of ID numbers.",
          },
          {
            title: "Radiologist Worklist",
            text: "The radiologist worklist is now available to Practice Admins.",
          },
        ],
      },
    ],
  },
];

export default function WhatsNewModal({ visible, onCancel, mandatory, onComplete }) {
  const [index, setIndex] = useState(0);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const sectionsRef = useRef(null);

  const updateScrollHint = () => {
    const el = sectionsRef.current;
    if (!el) {
      setShowScrollHint(false);
      return;
    }
    const scrollable = el.scrollHeight > el.clientHeight + 4;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 12;
    setShowScrollHint(scrollable && !atBottom);
  };

  useEffect(() => {
    if (!visible) return undefined;
    const el = sectionsRef.current;
    if (el) el.scrollTop = 0;
    const t = setTimeout(updateScrollHint, 60); // after layout settles
    return () => clearTimeout(t);
  }, [index, visible]);

  const items = RELEASE_SECTIONS;
  const item = items[Math.min(index, items.length - 1)];
  const isLast = index >= items.length - 1;

  const handleClose = () => {
    setIndex(0);
    onCancel();
  };

  const handleCancelAttempt = () => {
    if (mandatory) return;
    handleClose();
  };

  const handleNext = () => {
    if (isLast) {
      if (mandatory && onComplete) onComplete();
      handleClose();
    } else {
      setIndex(index + 1);
    }
  };

  return (
    <Modal
      visible={visible}
      onCancel={handleCancelAttempt}
      closable={false}
      maskClosable={!mandatory}
      keyboard={!mandatory}
      centered
      width={860}
      destroyOnClose
      className="whats-new-modal-v2"
      footer={null}
    >
      <div className="wn-card">
        <div className="wn-tabs">
          {items.map((it, i) => (
            <button
              key={it.tab}
              type="button"
              className={i === index ? "wn-tab active" : i < index ? "wn-tab done" : "wn-tab"}
              onClick={() => setIndex(i)}
            >
              {it.tab}
            </button>
          ))}
        </div>
        <div className="wn-main">
          <div className={item.launch ? "wn-hero wn-hero-launch" : "wn-hero"}>
            <div className="wn-hero-tile">
              <span className="wn-hero-icon" aria-hidden="true">{item.icon}</span>
            </div>
            {item.heroCaption && <span className="wn-hero-caption">{item.heroCaption}</span>}
          </div>
          <div className="wn-body">
            <span className={item.launch ? "wn-pill wn-pill-launch" : "wn-pill"}>✦ {item.pill || "What's New"}</span>
            <h2 className="wn-title">{item.title}</h2>
            {item.lead && <p className="wn-lead">{item.lead}</p>}
            <div className="wn-sections" ref={sectionsRef} onScroll={updateScrollHint}>
              {item.groups.map((group, gi) => (
                <div className="wn-group" key={group.heading || gi}>
                  {group.heading && <h4 className="wn-group-heading">{group.heading}</h4>}
                  <ul className="wn-points">
                    {group.points.map((point, pi) => {
                      if (typeof point === "string") {
                        return <li key={point}>{point}</li>;
                      }
                      return (
                        <li key={point.title || pi}>
                          {point.title && <span className="wn-point-title">{point.title}</span>}
                          {point.text && <span className="wn-point-text">{point.text}</span>}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
            {showScrollHint && (
              <div className="wn-scroll-hint" aria-hidden="true">Scroll ↓</div>
            )}
          </div>
        </div>
        <div className="wn-footer">
          <span className="wn-release-date">Release October 14th, 2026</span>
          <div className="wn-actions">
            {index > 0 ? (
              <Button className="wn-back" onClick={() => setIndex(index - 1)}>Back</Button>
            ) : !mandatory ? (
              <Button className="wn-back" onClick={handleClose}>Skip</Button>
            ) : null}
            <Button type="primary" className="wn-next" onClick={handleNext}>
              {isLast ? "Done" : "Next"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
